<template>
  <menu-layout :loading="loading" :title="$t('downloads.title')" :subtitle="$t('downloads.subtitle')">
    <row>
      <col12>
        <p class="mb-4 text-body-2">{{ $t('downloads.retention_note') }}</p>
        <v-alert
          v-if="!loading && items.length === 0"
          type="info"
          variant="tonal"
          class="mb-4"
          density="comfortable"
        >
          {{ $t('downloads.empty') }}
        </v-alert>
        <v-table v-if="items.length > 0" density="comfortable">
          <thead>
            <tr>
              <th>{{ $t('downloads.col_created') }}</th>
              <th>{{ $t('downloads.col_status') }}</th>
              <th>{{ $t('downloads.col_progress') }}</th>
              <th>{{ $t('downloads.col_wait') }}</th>
              <th>{{ $t('downloads.col_expires') }}</th>
              <th>{{ $t('downloads.col_actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in displayItems" :key="item.id">
              <td>{{ formatDate(item.created_at) }}</td>
              <td>
                <v-chip
                  size="small"
                  :color="statusColor(item)"
                  variant="tonal"
                >
                  {{ statusLabel(item) }}
                </v-chip>
                <div v-if="item.error_message" class="text-caption text-error mt-1">
                  {{ item.error_message }}
                </div>
              </td>
              <td>
                <div v-if="item.status === 'preparing' || item.status === 'queued'">
                  <v-progress-linear
                    :model-value="item.progress || 0"
                    height="8"
                    rounded
                    color="primary"
                    class="mb-1"
                    style="min-width: 120px"
                  />
                  <span class="text-caption">{{ progressText(item) }}</span>
                </div>
                <span v-else-if="item.status === 'ready'">100%</span>
                <span v-else>—</span>
              </td>
              <td class="text-caption">{{ waitText(item) || '—' }}</td>
              <td>{{ formatDate(item.expires_at) }}</td>
              <td>
                <v-btn
                  v-if="item.ready || item.status === 'ready'"
                  size="small"
                  color="primary"
                  variant="tonal"
                  class="mr-2"
                  :href="fileHref(item)"
                  download
                  id="download-crop-zip"
                >
                  <v-icon start size="small">fas fa-download</v-icon>
                  {{ $t('downloads.btn_download') }}
                </v-btn>
                <v-btn
                  size="small"
                  color="error"
                  variant="text"
                  @click="deleteItem(item)"
                >
                  {{ $t('downloads.btn_delete') }}
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
        <v-btn class="mt-4" variant="outlined" @click="load">
          {{ $t('downloads.btn_refresh') }}
        </v-btn>
      </col12>
    </row>
  </menu-layout>
</template>

<script>
import dayjs from 'dayjs';
import MenuLayout from '@/components/layout/MenuLayout';
import Row from '@/components/layout/Row';
import Col12 from '@/components/layout/Col12';
import MyAssetsJobsWebSocketMixin from '@/mixins/MyAssetsJobsWebSocketMixin.js';
import {
  cropDownloadProgressText,
  cropDownloadStatusLabel,
  cropDownloadWaitEstimate,
  mergeDownloadWithLiveJob,
} from '@/utils/cropDownloadStatus';

export default {
  name: 'Downloads',
  components: { MenuLayout, Row, Col12 },
  mixins: [MyAssetsJobsWebSocketMixin],
  data() {
    return {
      loading: false,
      items: [],
      tick: 0,
      tickTimer: null,
    };
  },
  computed: {
    liveJobs() {
      return this.$store.getters['project_live_jobs/crop_download_jobs'] || [];
    },
    displayItems() {
      // tick forces ETA refresh while jobs run
      void this.tick;
      return (this.items || []).map((item) => mergeDownloadWithLiveJob(item, this.liveJobs));
    },
  },
  watch: {
    liveJobs: {
      deep: true,
      handler() {
        this.loadQuiet();
      },
    },
  },
  async mounted() {
    await this.$store.dispatch('project_live_jobs/ensure_subscribed');
    await this.load();
    this.tickTimer = setInterval(() => {
      this.tick += 1;
    }, 5000);
  },
  beforeUnmount() {
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
    }
  },
  methods: {
    formatDate(value) {
      if (!value) {
        return '—';
      }
      return dayjs(value).format('YYYY-MM-DD HH:mm');
    },
    statusLabel(item) {
      return cropDownloadStatusLabel(item, this.$t.bind(this));
    },
    statusColor(item) {
      if (item.status === 'ready') return 'success';
      if (item.status === 'failed') return 'error';
      if (item.status === 'preparing') return 'primary';
      return 'grey';
    },
    progressText(item) {
      return cropDownloadProgressText(item);
    },
    waitText(item) {
      return cropDownloadWaitEstimate(item, this.$t.bind(this));
    },
    fileHref(item) {
      return this.$api.crop_download.fileUrl(item.id);
    },
    async load() {
      this.loading = true;
      try {
        await this.$store.dispatch('project_live_jobs/fetch_active');
        await this.loadQuiet();
      } finally {
        this.loading = false;
      }
    },
    async loadQuiet() {
      const data = await this.$api.crop_download.list();
      this.items = data?.items || [];
    },
    async deleteItem(item) {
      if (!window.confirm(this.$t('downloads.confirm_delete'))) {
        return;
      }
      await this.$api.crop_download.delete(item.id);
      await this.loadQuiet();
    },
  },
};
</script>
